if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 993px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.elements = {};
        this._initialized = false;
        this._boundOnSlideChanged = this.setAriaCurrentOnThumbnail.bind(this);
        this._boundOnThumbnailsReady = this.tryInitialize.bind(this);
        this._boundOnStyleChanged = this.onStyleChanged.bind(this);
        this._readyListenerElement = null;
      }

      connectedCallback() {
        this.cacheElements();
        if (!this.elements.thumbnails) return;

        if (this._readyListenerElement && this._readyListenerElement !== this.elements.thumbnails) {
          this._readyListenerElement.removeEventListener('slider:ready', this._boundOnThumbnailsReady);
        }
        if (this._readyListenerElement !== this.elements.thumbnails) {
          this.elements.thumbnails.addEventListener('slider:ready', this._boundOnThumbnailsReady);
          this._readyListenerElement = this.elements.thumbnails;
        }
        this.addEventListener('media-gallery:style-changed', this._boundOnStyleChanged);
        this.tryInitialize();
      }

      disconnectedCallback() {
        if (this._readyListenerElement) {
          this._readyListenerElement.removeEventListener('slider:ready', this._boundOnThumbnailsReady);
          this._readyListenerElement = null;
        }
        this.removeEventListener('media-gallery:style-changed', this._boundOnStyleChanged);
      }

      cacheElements() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
      }

      tryInitialize() {
        if (this._initialized || !this.elements.viewer || !this.elements.thumbnails) return;
        if (!this.elements.thumbnails.slider) return;

        // this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        this.elements.viewer.addEventListener('slideChanged', this._boundOnSlideChanged);
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;

          button.addEventListener('click', (e) => {
            e.preventDefault();
            const targetIndex = parseInt(mediaToSwitch.dataset.slideIndex);
            const targetSlide = this.elements.viewer.querySelector(`[data-slide-index="${targetIndex}"]`);

            if (targetSlide && this.elements.viewer.slider) {
              if (typeof this.elements.viewer.setSlidePosition === 'function') {
                this.elements.viewer.setSlidePosition(targetSlide.offsetLeft);
              } else {
                const scrollBehavior = this.reducedMotion.matches ? 'auto' : 'smooth';
                this.elements.viewer.slider.scrollTo({ left: targetSlide.offsetLeft, behavior: scrollBehavior });
              }
            }
          });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Initialize first thumbnail on page load
        const firstThumbnail = this.elements.thumbnails.querySelector('[data-slide-index="0"]');
        if (firstThumbnail) {
          this.setActiveThumbnail(firstThumbnail);
        }
        this.resetViewerToFirstSlide();

        if (this._readyListenerElement) {
          this._readyListenerElement.removeEventListener('slider:ready', this._boundOnThumbnailsReady);
          this._readyListenerElement = null;
        }

        this._initialized = true;
      }

      onStyleChanged(event) {
        if (event.target !== this) return;
        this.resetViewerToFirstSlide();
      }

      resetViewerToFirstSlide() {
        if (!this.elements.viewer || !this.elements.viewer.slider) return;

        if (typeof this.elements.viewer.lockPdpFirstSlide === 'function') {
          this.elements.viewer.lockPdpFirstSlide();
        }
        if (typeof this.elements.viewer.resetPages === 'function') {
          this.elements.viewer.resetPages();
        }
        this.elements.viewer.slider.scrollLeft = 0;
        if (typeof this.elements.viewer.maintainPdpFirstSlide === 'function') {
          this.elements.viewer.maintainPdpFirstSlide();
        }

        if (this.elements.thumbnails) {
          const firstThumbnail = this.elements.thumbnails.querySelector('[data-slide-index="0"]');
          if (firstThumbnail) {
            this.setActiveThumbnail(firstThumbnail);
          }
        }
      }

      setAriaCurrentOnThumbnail(event) {
        if (!this.elements.thumbnails || !event.detail || !event.detail.currentElement) return;

        if (this.elements.viewer && this.elements.viewer._isPdpFirstSlideLocked) {
          const firstThumbnail = this.elements.thumbnails.querySelector('[data-slide-index="0"]');
          if (firstThumbnail) {
            this.setActiveThumbnail(firstThumbnail);
          }
          return;
        }

        const gallerySlide = event.detail.currentElement;
        const slideIndex = gallerySlide.dataset.slideIndex;
        this.elements.thumbnails.querySelectorAll('[data-slide-index]').forEach((thumb) => {
          thumb.querySelector('button').removeAttribute('aria-current');
        });
        const thumbnail = this.elements.thumbnails.querySelector(`[data-slide-index="${slideIndex}"]`);
        if (thumbnail) {
          thumbnail.querySelector('button').setAttribute('aria-current', true);
        }
      }

      onSlideChanged(event) {
        const gallerySlide = event.detail.currentElement;
        const slideIndex = gallerySlide.dataset.slideIndex;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-slide-index="${slideIndex}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const scrollBehavior = this.reducedMotion.matches ? 'auto' : 'smooth';
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: scrollBehavior });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: scrollBehavior });
          }
          // Trigger slider update after scroll completes
          if (this.elements.viewer.slider && this.elements.viewer.slider.update) {
            this.elements.viewer.slider.update();
          }
        }, 300);
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (typeof this.elements.thumbnails.isSlideVisible === 'function' && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) {
          return;
        }

        if (!this.elements.thumbnails.slider) return;

        const scrollBehavior = this.reducedMotion.matches ? 'auto' : 'smooth';
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: scrollBehavior });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        //window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
